import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../../../style/senior/SeniorCollegePlacementResult.css'; // Local styling
import { PDFDownloadButton, PDFViewButton } from './srCollegeUIWrapper';
import LoadingPage from '../../../Loader';

import { InvoiceDownloadButton, InvoiceViewButton } from '../../../pages/payment/Invoice/InvoicePDFButtons';

import ErrorPage from '../../../pages/dataSubmit/404PageNotFound';
import SubmissionConfirmation from '../../../pages/dataSubmit/dataSubmitted';
import { CallMailDetails } from './srPlacementMail';

// Utility function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "N/A"; // Handle empty values
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const SeniorCollegePlacementResult = () => {
  const { uniqueFormId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState({});
  const [emailSent, setEmailSent] = useState(false);
  const [httpStatus, setHttpStatus] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/srPlacementForm/srPlacement-result/${uniqueFormId}`
        );

        setHttpStatus(response.status);

        if (response.status === 403) {
        const data = await response.json();
        setTimestamp(data.timestamp); 
        return; 
      }

      if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();

        const formattedInfo = {
          ...data.info,
          placementDate: new Date(data.info.placementDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
        };

        setInfo(formattedInfo);
        setResult(data.result || null);


        
          // ✅ Send only once per mount
      if (!emailSent) {
        setEmailSent(true);
        setTimeout(() => {
          CallMailDetails(data.info);
        }, 500);
      }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uniqueFormId, emailSent]);
  
  if (loading) return <LoadingPage />;
  if (httpStatus === 403) {
   return (
     <SubmissionConfirmation
       uniqueFormId={uniqueFormId}
       timestamp={timestamp}
     />
   );
 }
 
 // Return nothing (blank page) for 404
 if (httpStatus === 404) {
   return <ErrorPage/>;
 }
  return (
    <div className="senior-placement-container">
      <p className="senior-placement-header">
        Accompaniment to Government Resolution, Higher and Technical Education Department No. Resolution No.
        Misc-2018/C.R.56/18/UNI-1, dated 08 March, 2019.
      </p>
      <h1 className="senior-placement-title">PROFORMA FOR FIXATION OF PAY on Promotion In 7th Pay</h1>

      {/* Information Section */}
      <div className="senior-placement-info">
        <p>
          <strong>Name of the College/Institution:</strong> {info.officeName}
        </p>
        <p>
          <strong>Name of the teacher:</strong> {info.employeeName}
        </p>
      </div>

      {/* Table Section */}
      <table className="senior-placement-table-main">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Description</th>
              <th>Information</th> {/* ✅ Third column restored */}
            </tr>
          </thead>
          <tbody>
            {[
              { sr: 1, desc: `Designation of the post in which pay is to be fixed as on ${info.placementDate}`, info: info.revisedDesignation },
              { sr: 2, desc: 'Status (Substantive/officiating)', info: 'Substantive' },
              { sr: 3, desc: 'Date of Placement', info: info.placementDate },
              { sr: 4, desc: 'Pre-revised Designation', info: info.existingDesignation },
              { sr: 5, desc: 'Pre-revised Academic Level & pay Matrix Scale', info: info.existingPayLevel },
              { sr: 6, desc: 'Pre-revised Pay in the pay matrix scale as on date of placement', info: info.existingPayInLevel },
              { sr: 7, desc: 'Pay after the notional increment in the Pre-revised scale in Matrix', info: info.afterNotionalIncrement },
              { sr: 8, desc: 'Revised Pay Scale in the Pay Matrix & Academic Level of Promotion Post', info: info.revisedPayLevel },
              { sr: 9, desc: 'Applicable Cell in the Level either equal to or just above the Amount at Sr. No. 6 (For Promotion)', info: info.revisedPay },
              { sr: 10, desc: 'Date of next increment and pay after grant of increment', info: `${formatDate(info.nextApplicableDate)} - ${result[0][2]}` },
            ].map((row) => (
              <tr key={row.sr}>
                <td>{row.sr}</td>
                <td>{row.desc}</td>
                <td>{row.info}</td>
              </tr>
            ))}

            {/* ✅ Sr. No. 11 - Merging Second & Third Columns for Nested Table */}
            <tr>
              <td>11</td>
              <td colSpan="2"> {/* ✅ Merging Columns */}
                <strong>Pay after increment in applicable Level of pay Matrix</strong>
                {Array.isArray(result) && result.length > 0 ? (
                   <table className="senior-nested-table-specific">
                    <thead>
                      <tr>
                        <th>Date of Next Increment</th>
                        <th>Applicable Level</th>
                        <th>Pay after Increment in Pay Matrix</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          <td>{formatDate(row[0])}</td> {/* ✅ Date formatted */}
                          <td>{row[1] || "N/A"}</td>
                          <td>{row[2] || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No next increment data available</p>
                )}
              </td>
            </tr>
            <tr>

            <td>12</td>
            <td>Remark</td>
            <td>{info.remark || ''}</td>
          </tr>
          </tbody>

        </table>

        <div className="mt-8">
  {/* Header */}
  <div className="mb-6">
    <h3 className="text-lg font-semibold text-gray-800">Document Actions</h3>
    <p className="text-sm text-gray-500">Generate and download official documents</p>
  </div>

  {/* Actions Grid */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {/* PDF Section */}
    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-center mb-4">
        <div className="p-2 bg-blue-100 rounded-md mr-3">
          <span className="text-blue-600 font-bold">PDF</span>
        </div>
        <h4 className="text-md font-medium text-gray-700">Senior College Placement Document</h4>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3">
      <PDFDownloadButton result={result} info={info} />
      <PDFViewButton result={result} info={info} />
      </div>
    </div>

    {/* Invoice Section */}
    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-center mb-4">
        <div className="p-2 bg-green-100 rounded-md mr-3">
          <span className="text-green-600 font-bold">INV</span>
        </div>
        <h4 className="text-md font-medium text-gray-700">Payment Invoice</h4>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3">
      <InvoiceDownloadButton 
        employeeName={info.employeeName || ''} 
        formName="Senior College Placement"
        amount={249}  
        transactionID={info.transactionID}
        timestamp={info.timestamp}
      />
        <InvoiceViewButton 
        employeeName={info.employeeName || ''} 
        formName="Senior College Placement"
        amount={249}  
        transactionID={info.transactionID}
        timestamp={info.timestamp}
      />
      </div>
    </div>
  </div>
  </div>

    </div>
  );
};

export default SeniorCollegePlacementResult;
